import axios from 'axios';
import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';
// @ts-ignore
import qs from 'qs';

export interface AuthServiceProps {
  getUser(): Promise<User | null>;
  login(): Promise<void>;
  logout(): Promise<void>;
  getAccessToken(): Promise<string | null>;
  getRefreshToken(): string | null;
  renewToken(): Promise<User | null>;
  signinCallback(): Promise<User>;
  startTokenAutoRefresh(): void;
  stopTokenAutoRefresh(): void;
  userManager: UserManager;
}

export const useAuthService = (
  // @ts-ignore
  authority: string = UMI_KEY_CLOCK_URL,
  // @ts-ignore
  client_id: string = UMI_KEY_CLOCK_CLIENT_ID,
): AuthServiceProps => {
  const oidcSettings: UserManagerSettings = {
    authority,
    client_id,
    post_logout_redirect_uri: `${window.location.origin}/login`,
    redirect_uri: `${window.location.origin}`,
    response_type: 'code',
    scope: 'openid profile email',
    response_mode: 'query',
    loadUserInfo: true,
    automaticSilentRenew: true, // 启用自动静默刷新
    silent_redirect_uri: `${window.location.origin}/silent-renew.html`, // 静默刷新回调页面
    accessTokenExpiringNotificationTimeInSeconds: 60, // 在token过期前60秒触发事件
    // userStore: new WebStorageStateStore({ store: window.localStorage }),
  };

  // 本地开发 退出 手动处理
  if (window.location.origin === 'http://localhost:8000') {
    // @ts-ignore
    delete oidcSettings.post_logout_redirect_uri;
  }

  const userManager = new UserManager(oidcSettings);
  let tokenRefreshTimer: NodeJS.Timeout | null = null;

  // 将Oidc对象暴露到全局作用域，供静默刷新页面使用
  if (typeof window !== 'undefined') {
    // @ts-ignore
    window.Oidc = { UserManager, User };
  }

  // 处理用户信息
  const handleUserInfo = (user: User | null) => {
    if (user) {
      localStorage.setItem('access_token', user.access_token || '');
      localStorage.setItem('refresh_token', user.refresh_token || '');
      if (user.expires_at) {
        localStorage.setItem('expireTime', user.expires_at.toString());
      }
      console.log('用户信息已更新，token过期时间:', user.expires_at);
    }
  };

  // 清理本地存储的token
  const clearTokens = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('expireTime');
  };

  // 获取用户信息
  const getUser = (): Promise<User | null> => {
    return userManager
      .getUser()
      .then((user) => {
        handleUserInfo(user);
        return user;
      })
      .catch((error) => {
        console.error('获取用户信息失败:', error);
        clearTokens();
        return null;
      });
  };

  // 获取访问令牌（带自动刷新功能）
  const getAccessToken = async (): Promise<string | null> => {
    try {
      // 首先尝试从userManager获取最新用户信息
      const user = await userManager.getUser();

      if (user && !user.expired) {
        handleUserInfo(user);
        return user.access_token;
      }

      // 如果token已过期，尝试静默刷新
      if (user && user.expired) {
        console.log('检测到token已过期，尝试静默刷新...');
        const refreshedUser = await renewToken();
        return refreshedUser?.access_token || null;
      }

      return null;
    } catch (error) {
      console.error('获取访问令牌失败:', error);
      return null;
    }
  };

  // 静默刷新token
  const renewToken = async (): Promise<User | null> => {
    try {
      console.log('开始静默刷新token...');
      const user = await userManager.signinSilent();
      handleUserInfo(user);
      console.log('token静默刷新成功');
      return user;
    } catch (error) {
      console.error('token静默刷新失败:', error);

      // 如果静默刷新失败，尝试使用refresh_token手动刷新
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        console.log('尝试使用refresh_token手动刷新...');
        return await manualTokenRefresh(refreshToken);
      }

      // 刷新失败，清理token并跳转到登录页
      clearTokens();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return null;
    }
  };

  // 手动刷新token（备用方案）
  const manualTokenRefresh = async (
    refreshToken: string,
  ): Promise<User | null> => {
    try {
      const response = await axios.post(
        // @ts-ignore
        `${UMI_KEY_CLOCK_URL}protocol/openid-connect/token`,
        qs.stringify({
          // @ts-ignore
          client_id: UMI_KEY_CLOCK_CLIENT_ID,
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      );

      const { access_token, refresh_token, expires_in } = response.data;

      // 创建新的User对象
      const user = new User({
        access_token,
        refresh_token,
        expires_in,
        token_type: 'Bearer',
        scope: 'openid profile email',
        // @ts-ignore
        profile: {},
      });

      // 存储到userManager
      await userManager.storeUser(user);
      handleUserInfo(user);

      console.log('手动token刷新成功');
      return user;
    } catch (error) {
      console.error('手动token刷新失败:', error);
      throw error;
    }
  };

  // 获取刷新令牌
  const getRefreshToken = (): string | null => {
    return localStorage.getItem('refresh_token');
  };

  // 登录
  const login = (): Promise<void> => {
    return userManager.signinRedirect();
  };

  // 登出
  const logout = async (): Promise<void> => {
    clearTokens();
    stopTokenAutoRefresh();
    return userManager.signoutRedirect();
  };

  // 登录回调
  const signinCallback = async (): Promise<User> => {
    const user = await userManager.signinRedirectCallback();
    handleUserInfo(user);
    startTokenAutoRefresh(); // 登录成功后启动自动刷新
    return user;
  };

  // 启动token自动刷新
  const startTokenAutoRefresh = () => {
    console.log('启动token自动刷新监听器');

    // 监听token即将过期事件
    userManager.events.addAccessTokenExpiring(() => {
      console.log('访问令牌即将过期，开始自动刷新...');
      renewToken().catch((error) => {
        console.error('自动刷新失败:', error);
      });
    });

    // 监听token过期事件
    userManager.events.addAccessTokenExpired(() => {
      console.log('访问令牌已过期');
      clearTokens();
    });

    // 监听用户已加载事件
    userManager.events.addUserLoaded((user) => {
      console.log('用户已加载，更新token信息');
      handleUserInfo(user);
    });

    // 监听用户已卸载事件
    userManager.events.addUserUnloaded(() => {
      console.log('用户已卸载');
      clearTokens();
    });

    // 设置定时检查（每5分钟检查一次token状态）
    tokenRefreshTimer = setInterval(async () => {
      try {
        const user = await userManager.getUser();
        if (user && user.expired) {
          console.log('定时检查发现token已过期，尝试刷新...');
          await renewToken();
        }
      } catch (error) {
        console.error('定时检查token状态失败:', error);
      }
    }, 5 * 60 * 1000); // 5分钟
  };

  // 停止token自动刷新
  const stopTokenAutoRefresh = () => {
    console.log('停止token自动刷新监听器');

    // 移除所有事件监听器
    userManager.events.removeAccessTokenExpiring(() => {});
    userManager.events.removeAccessTokenExpired(() => {});
    userManager.events.removeUserLoaded(() => {});
    userManager.events.removeUserUnloaded(() => {});

    // 清除定时器
    if (tokenRefreshTimer) {
      clearInterval(tokenRefreshTimer);
      tokenRefreshTimer = null;
    }
  };

  return {
    getUser,
    login,
    logout,
    getAccessToken,
    getRefreshToken,
    renewToken,
    signinCallback,
    startTokenAutoRefresh,
    stopTokenAutoRefresh,
    userManager,
  };
};
